import { useEffect, useRef } from 'react'
import {
  formatGoalPeriodAnchorLabel,
  getCalendarPeriodBounds,
  getDefaultPeriodAnchor,
  isGoalMetricPeriodExpired,
  parsePeriodAnchor,
  periodStartFromStoredValue,
} from '../../../utils/goalMetricPeriodUtils'
import { periodValueForApi } from '../../../lib/utils/clientMetricGoals'
import './GoalsSummaryHeader.css'

const REACH_OPTIONS = ['Organic', 'Paid', 'Mixed']
const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]

const MONTH_OPTIONS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
]

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1' },
  { value: 2, label: 'Q2' },
  { value: 3, label: 'Q3' },
  { value: 4, label: 'Q4' },
]

function buildYearOptions(referenceYear = new Date().getFullYear()) {
  return Array.from({ length: 8 }, (_, index) => referenceYear - 1 + index)
}

function getMetricReach(metric, goalMetricReach) {
  return goalMetricReach?.[metric.id] ?? metric?.source ?? 'Organic'
}

function resolveMetricTargetValue(goalMetricTargets, metricId, metric) {
  const stored = goalMetricTargets?.[metricId]
  if (stored !== undefined && stored !== null) return stored
  return metric?.defaultTarget ?? ''
}

function metricTargetHasValue(value) {
  if (value === '' || value == null) return false
  const n = Number(value)
  return !Number.isNaN(n)
}

function parseMetricTargetInput(raw) {
  if (raw === '') return ''
  const n = Number(raw)
  return Number.isNaN(n) ? '' : n
}

const IndeterminateCheckbox = ({ checked, indeterminate, className, ...props }) => {
  const checkboxRef = useRef(null)

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      checked={checked}
      className={className}
      {...props}
    />
  )
}

/**
 * Goal naming, section title, compact metric filters (time, platform), then metric list.
 */
export default function GoalsMetricsConfigurator({
  isGoalsReadOnly,
  goalsMetricListRef,
  filteredGoalMetricGroups,
  selectedGoalMetrics,
  expandedGoalGroups,
  toggleGoalMetricGroup,
  toggleGoalMetricGroupExpanded,
  toggleGoalMetric,
  goalMetricTargets,
  setGoalMetricTargets,
  goalMetricPeriods,
  setGoalMetricPeriods,
  goalMetricReach,
  setGoalMetricReach,
  goalMetricPeriodStarts = {},
  setGoalMetricPeriodStarts,
  goalMetricPeriodValues = {},
  setGoalMetricPeriodValues,
  goalPeriodEndedAcknowledged = new Set(),
  onRequestExtendGoal,
  editingMetricIds,
  setEditingMetricIds,
  onMetricGoalDone,
  onMetricGoalEditStart,
  onMetricGoalCancel,
  formatGoalValue,
  targetsHelpTooltip = 'Turn on metrics below, adjust targets, then press “Save & Activate”.',
  metricsSectionMode = 'setup',
  setupPreviewMode = false,
  onDeleteGoal,
  activeStrategyTitle,
  onModifyGoals,
  onCancelEdit,
  onSaveEdit,
  saveEditDisabled = false,
  showMetricFilters = false,
  goalsTimeRef,
  goalsTimeOpen,
  setGoalsTimeOpen,
  goalsTimeRange,
  setGoalsTimeRange,
  goalsTimeRangeLabel,
  goalsPlatformFilterRef,
  goalsPlatformFilterOpen,
  setGoalsPlatformFilterOpen,
  goalsPlatformFilter,
  setGoalsPlatformFilter,
  goalsPlatformFilterLabel,
  goalsPlatformOptions = [],
}) {
  const showModifyInline =
    metricsSectionMode === 'edit' && isGoalsReadOnly && typeof onModifyGoals === 'function'
  const metricsTitle =
    metricsSectionMode === 'edit' && activeStrategyTitle
      ? activeStrategyTitle
      : 'Set targets'
  const showEditActions =
    metricsSectionMode === 'edit' &&
    !isGoalsReadOnly &&
    typeof onCancelEdit === 'function' &&
    typeof onSaveEdit === 'function'
  const railAligned = showModifyInline || showEditActions
  const showDeleteGoalButton = false
  const yearOptions = buildYearOptions()

  const applyPeriodBounds = (metricId, period, bounds) => {
    if (!bounds) return
    setGoalMetricPeriodStarts?.((prev) => ({
      ...prev,
      [metricId]: bounds.start,
    }))
    const nextPeriodValue = periodValueForApi(period, bounds.start)
    if (nextPeriodValue) {
      setGoalMetricPeriodValues?.((prev) => ({
        ...prev,
        [metricId]: nextPeriodValue,
      }))
    }
  }

  const handlePeriodChange = (metricId, period) => {
    setGoalMetricPeriods((prev) => ({
      ...prev,
      [metricId]: period,
    }))
    applyPeriodBounds(metricId, period, getCalendarPeriodBounds(period, getDefaultPeriodAnchor(period)))
  }

  const handlePeriodAnchorChange = (metricId, period, periodStart, anchorPatch) => {
    const bounds = getCalendarPeriodBounds(period, {
      ...parsePeriodAnchor(period, periodStart),
      ...anchorPatch,
    })
    applyPeriodBounds(metricId, period, bounds)
  }

  const renderPeriodAnchorFields = (metricId, periodValue, periodStart, isSelected, metricFieldsLocked) => {
    const anchor = parsePeriodAnchor(periodValue, periodStart)
    const disabled = !isSelected || metricFieldsLocked

    if (periodValue === 'annual') {
      return (
        <select
          className="goalsMetricSelect goalsMetricSelect--periodAnchor"
          value={anchor.year}
          disabled={disabled}
          aria-label="Goal year"
          onChange={(e) =>
            handlePeriodAnchorChange(metricId, periodValue, periodStart, {
              year: Number(e.target.value),
            })
          }
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      )
    }

    if (periodValue === 'monthly') {
      return (
        <>
          <select
            className="goalsMetricSelect goalsMetricSelect--periodAnchor"
            value={anchor.month}
            disabled={disabled}
            aria-label="Goal month"
            onChange={(e) =>
              handlePeriodAnchorChange(metricId, periodValue, periodStart, {
                month: Number(e.target.value),
              })
            }
          >
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            className="goalsMetricSelect goalsMetricSelect--periodAnchor"
            value={anchor.year}
            disabled={disabled}
            aria-label="Goal year"
            onChange={(e) =>
              handlePeriodAnchorChange(metricId, periodValue, periodStart, {
                year: Number(e.target.value),
              })
            }
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </>
      )
    }

    if (periodValue === 'quarterly') {
      return (
        <>
          <select
            className="goalsMetricSelect goalsMetricSelect--periodAnchor"
            value={anchor.quarter}
            disabled={disabled}
            aria-label="Goal quarter"
            onChange={(e) =>
              handlePeriodAnchorChange(metricId, periodValue, periodStart, {
                quarter: Number(e.target.value),
              })
            }
          >
            {QUARTER_OPTIONS.map((quarter) => (
              <option key={quarter.value} value={quarter.value}>
                {quarter.label}
              </option>
            ))}
          </select>
          <select
            className="goalsMetricSelect goalsMetricSelect--periodAnchor"
            value={anchor.year}
            disabled={disabled}
            aria-label="Goal year"
            onChange={(e) =>
              handlePeriodAnchorChange(metricId, periodValue, periodStart, {
                year: Number(e.target.value),
              })
            }
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </>
      )
    }

    return null
  }

  return (
    <>
      <div className={`goalsSectionHeader ${railAligned ? 'goalsSectionHeader--railAligned' : ''}`}>
        <div>
          <div className="goalsSectionTitleRow goalsSectionTitleRow--withActions">
            <div className="goalsSectionTitle">{metricsTitle}</div>
            {showDeleteGoalButton && (
              <button
                type="button"
                className="goalsDeleteGoalButton"
                onClick={onDeleteGoal}
                aria-label="Delete goal and all metrics"
              >
                Delete Goal
              </button>
            )}
          </div>
        </div>
        {showModifyInline && (
          <button
            type="button"
            className="goalsModifyButton"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onModifyGoals()
            }}
            aria-label="Modify goals"
          >
            Modify goals
          </button>
        )}
        {showEditActions && (
          <div className="goalsSummaryHeaderActions">
            <button type="button" className="goalsCancelButton" onClick={onCancelEdit} aria-label="Cancel">
              Cancel
            </button>
            <button
              type="button"
              className="goalsSaveButton"
              onClick={onSaveEdit}
              disabled={saveEditDisabled}
              aria-label="Save changes"
            >
              {saveEditDisabled ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {showMetricFilters && (
          <div className="goalsFiltersBar goalsFiltersBar--underMetricsTitle goalsFiltersBar--compactMetrics">
            <div className="goalsToolbarRight goalsToolbarRight--metricFiltersCompact">
              <span className="goalsFilterByLabel">Filter by:</span>
              <div className="goalsToolbarGroup goalsToolbarGroup--metricFilter">
                <div ref={goalsTimeRef} className="goalsSelect">
                  <button
                    type="button"
                    id="goalsTimeRangeSelectMetrics"
                    className="goalsSelectButton"
                    aria-haspopup="listbox"
                    aria-expanded={goalsTimeOpen}
                    onClick={() => setGoalsTimeOpen((prev) => !prev)}
                  >
                    <span className="goalsSelectLabel">Time period</span>
                    <span className="goalsSelectValue">{goalsTimeRangeLabel}</span>
                  </button>
                  {goalsTimeOpen && (
                    <div className="goalsSelectMenu" role="listbox" aria-label="Time period">
                      {['month', 'quarter', 'year'].map((range) => (
                        <button
                          key={range}
                          type="button"
                          className={`goalsSelectOption ${goalsTimeRange === range ? 'goalsSelectOption--active' : ''}`}
                          onClick={() => {
                            setGoalsTimeRange(range)
                            setGoalsTimeOpen(false)
                          }}
                        >
                          {range === 'month' ? 'Monthly' : range === 'quarter' ? 'Quarterly' : 'Annual'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="goalsToolbarGroup goalsToolbarGroup--metricFilter">
                <div ref={goalsPlatformFilterRef} className="goalsSelect">
                  <button
                    type="button"
                    id="goalsPlatformFilterSelectMetrics"
                    className="goalsSelectButton"
                    aria-haspopup="listbox"
                    aria-expanded={goalsPlatformFilterOpen}
                    onClick={() => setGoalsPlatformFilterOpen((prev) => !prev)}
                  >
                    <span className="goalsSelectLabel">Platform</span>
                    <span className="goalsSelectValue">{goalsPlatformFilterLabel}</span>
                  </button>
                  {goalsPlatformFilterOpen && (
                    <div className="goalsSelectMenu" role="listbox" aria-label="Platform filter">
                      {goalsPlatformOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`goalsSelectOption ${goalsPlatformFilter === opt.value ? 'goalsSelectOption--active' : ''}`}
                          onClick={() => {
                            setGoalsPlatformFilter(opt.value)
                            setGoalsPlatformFilterOpen(false)
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
      )}

      <div ref={goalsMetricListRef} className="goalsMetricList">
        {filteredGoalMetricGroups.map((group) => {
          const selectedCount = group.metrics.filter((metric) => selectedGoalMetrics.has(metric.id)).length
          const isGroupChecked = selectedCount === group.metrics.length
          const isGroupIndeterminate = selectedCount > 0 && selectedCount < group.metrics.length
          const isGroupExpanded = expandedGoalGroups.has(group.id)
          return (
            <div key={group.id} className="goalsMetricGroup">
              <div className="goalsMetricGroupHeader">
                <div className="goalsMetricGroupHeaderLeft">
                  <label className="goalsMetricGroupToggle">
                    <IndeterminateCheckbox
                      className="dashboardConfigureCheckbox goalsMetricCheckbox"
                      checked={isGroupChecked}
                      indeterminate={isGroupIndeterminate}
                      onChange={() => toggleGoalMetricGroup(group.metrics.map((metric) => metric.id))}
                      disabled={isGoalsReadOnly}
                    />
                    <span className="goalsMetricGroupTitle">{group.label}</span>
                  </label>
                  <div className="goalsMetricGroupCount">
                    {selectedCount} of {group.metrics.length} active
                  </div>
                </div>
                <div className="goalsMetricGroupHeaderRight">
                  <span
                    className={`goalsMetricGroupStatus ${selectedCount === 0 ? 'goalsMetricGroupStatus--muted' : ''}`}
                  >
                    {selectedCount === 0
                      ? 'Not tracking'
                      : `Tracking ${selectedCount} metric${selectedCount === 1 ? '' : 's'}`}
                  </span>
                  <button
                    className="goalsMetricGroupToggleButton"
                    type="button"
                    aria-expanded={isGroupExpanded}
                    aria-label={isGroupExpanded ? 'Collapse' : 'Expand'}
                    onClick={() => toggleGoalMetricGroupExpanded(group.id)}
                  >
                    <span className="goalsMetricGroupToggleIcon" aria-hidden="true">
                      {isGroupExpanded ? '−' : '+'}
                    </span>
                  </button>
                </div>
              </div>
              {isGroupExpanded && (() => {
                const crossPlatformMetrics = group.metrics.filter(
                  (m) => m.platform === 'All Platforms' || m.source === 'Mixed'
                )
                const regularMetrics = group.metrics.filter(
                  (m) => m.platform !== 'All Platforms' && m.source !== 'Mixed'
                )
                const periodLabel = (p) => (p === 'monthly' ? 'Monthly' : p === 'quarterly' ? 'Quarterly' : 'Annual')
                const renderMetricCard = (metric) => {
                  const isSelected = selectedGoalMetrics.has(metric.id)
                  const targetValue = resolveMetricTargetValue(goalMetricTargets, metric.id, metric)
                  const periodValue = ['monthly', 'quarterly', 'annual'].includes(
                    goalMetricPeriods[metric.id] ?? metric?.defaultPeriod,
                  )
                    ? (goalMetricPeriods[metric.id] ?? metric?.defaultPeriod ?? 'monthly')
                    : 'monthly'
                  const reachValue = getMetricReach(metric, goalMetricReach)
                  const apiPeriodValue = goalMetricPeriodValues[metric.id]
                  const periodStart =
                    goalMetricPeriodStarts[metric.id] ||
                    periodStartFromStoredValue(periodValue, apiPeriodValue)
                  const periodRangeTag = formatGoalPeriodAnchorLabel(periodValue, periodStart)
                  const isPeriodExpired =
                    Boolean(periodStart) && isGoalMetricPeriodExpired(periodStart, periodValue)
                  const periodEndedAcknowledged = goalPeriodEndedAcknowledged.has(metric.id)
                  const showExtendAction =
                    isPeriodExpired &&
                    periodEndedAcknowledged &&
                    isGoalsReadOnly &&
                    typeof onRequestExtendGoal === 'function'
                  const isEditing = editingMetricIds.has(metric.id)
                  const metricFieldsLocked =
                    isGoalsReadOnly || (setupPreviewMode && !isEditing)
                  const hasTarget = metricTargetHasValue(targetValue)
                  const targetDisplay = formatGoalValue?.(targetValue, metric) ?? String(targetValue ?? '—')
                  return (
                    <div
                      key={metric.id}
                      className={`goalsMetricRow goalsMetricRow--card ${isSelected ? 'goalsMetricRow--active' : 'goalsMetricRow--unchecked'} ${isEditing ? 'goalsMetricRow--editing' : ''}`}
                    >
                      <div className="goalsMetricHeader">
                        <div className="goalsMetricHeaderLeft">
                          <label className="goalsMetricCheck">
                            <input
                              type="checkbox"
                              className="dashboardConfigureCheckbox goalsMetricCheckbox"
                              checked={isSelected}
                              onChange={() => toggleGoalMetric(metric.id)}
                              disabled={isGoalsReadOnly}
                            />
                            <span className="goalsMetricName">{metric.name}</span>
                          </label>
                          <div className="goalsMetricMeta">
                            {metric.platform && <span>{metric.platform}</span>}
                            <span>{reachValue}</span>
                            {isSelected && (
                              <span>{periodRangeTag}</span>
                            )}
                          </div>
                        </div>
                        <div className="goalsMetricHeaderRight">
                          {isEditing ? (
                            <div className="goalsMetricHeaderActions">
                              <button
                                type="button"
                                className="goalsMetricCancelIcon"
                                onClick={() => {
                                  if (typeof onMetricGoalCancel === 'function') {
                                    onMetricGoalCancel(metric.id)
                                  } else {
                                    setEditingMetricIds((prev) => {
                                      const next = new Set(prev)
                                      next.delete(metric.id)
                                      return next
                                    })
                                  }
                                }}
                                data-tooltip="Cancel"
                                aria-label="Cancel editing"
                              >
                                <img src="/assets/icons/common/close.svg" alt="" width={16} height={16} />
                              </button>
                              <button
                                type="button"
                                className="goalsMetricDoneIcon"
                                onClick={async () => {
                                  if (typeof onMetricGoalDone === 'function') {
                                    await onMetricGoalDone(metric.id)
                                  } else {
                                    setEditingMetricIds((prev) => {
                                      const next = new Set(prev)
                                      next.delete(metric.id)
                                      return next
                                    })
                                  }
                                }}
                                data-tooltip="Done"
                                aria-label="Done editing"
                              >
                                <img src="/assets/icons/common/check-enable.svg" alt="" width={16} height={16} />
                              </button>
                            </div>
                          ) : (
                            isSelected && (
                              <button
                                type="button"
                                className="goalsMetricEditIcon"
                                onClick={() => {
                                  if (typeof onMetricGoalEditStart === 'function') {
                                    onMetricGoalEditStart(metric.id)
                                  } else {
                                    setEditingMetricIds((prev) => new Set(prev).add(metric.id))
                                  }
                                }}
                                disabled={isGoalsReadOnly && !setupPreviewMode}
                                data-tooltip="Edit"
                                aria-label="Edit"
                              >
                                <img src="/assets/icons/common/edit.svg" alt="" width={16} height={16} />
                              </button>
                            )
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="goalsMetricControls goalsMetricControls--compact">
                          <div className="goalsMetricControl">
                            <label>Target Value</label>
                            <input
                              type="number"
                              step={
                                metric.format === 'percent' || metric.format === 'ratio' || metric.format === 'stars'
                                  ? '0.1'
                                  : '1'
                              }
                              value={targetValue === '' ? '' : targetValue}
                              disabled={!isSelected || metricFieldsLocked}
                              onChange={(e) =>
                                setGoalMetricTargets((prev) => ({
                                  ...prev,
                                  [metric.id]: parseMetricTargetInput(e.target.value),
                                }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                }
                              }}
                            />
                            {metric.helper && <span className="goalsMetricHelper">{metric.helper}</span>}
                          </div>
                          <div className="goalsMetricControl goalsMetricControl--reach">
                            <label htmlFor={`goal-reach-${metric.id}`}>Reach</label>
                            <select
                              id={`goal-reach-${metric.id}`}
                              className="goalsMetricSelect goalsMetricSelect--reach"
                              value={reachValue}
                              disabled={!isSelected || metricFieldsLocked}
                              onChange={(e) =>
                                setGoalMetricReach((prev) => ({
                                  ...prev,
                                  [metric.id]: e.target.value,
                                }))
                              }
                            >
                              {REACH_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="goalsMetricControl goalsMetricControl--period">
                            <label>Duration</label>
                            <div className="goalsMetricDurationControls">
                              <div className="goalsMetricPeriod goalsMetricPeriod--nowrap" role="group" aria-label="Duration">
                                {PERIOD_OPTIONS.map((period) => (
                                  <button
                                    key={period.value}
                                    type="button"
                                    className={`goalsMetricPill ${periodValue === period.value ? 'is-active' : ''}`}
                                    onClick={() => handlePeriodChange(metric.id, period.value)}
                                    disabled={!isSelected || metricFieldsLocked}
                                  >
                                    {period.label}
                                  </button>
                                ))}
                              </div>
                              {renderPeriodAnchorFields(
                                metric.id,
                                periodValue,
                                periodStart,
                                isSelected,
                                metricFieldsLocked,
                              )}
                            </div>
                            <span className="goalsMetricHelperSlot" aria-hidden="true" />
                          </div>
                        </div>
                      ) : (
                        isSelected && (
                          <div className="goalsMetricCardSummary">
                            <span className="goalsMetricSummaryLine">
                              Target: {targetDisplay} · Period: {periodLabel(periodValue)}
                              {periodStart ? ` · ${formatGoalPeriodAnchorLabel(periodValue, periodStart)}` : ''}
                              {' '}
                              · Reach: {reachValue}
                            </span>
                            {!hasTarget && (
                              <span className="goalsMetricTargetWarning">Target needed</span>
                            )}
                            {metric.helper && (
                              <span className="goalsMetricHelperInline">{metric.helper}</span>
                            )}
                            {showExtendAction && (
                              <button
                                type="button"
                                className="goalsMetricExtendButton"
                                onClick={() => onRequestExtendGoal(metric.id)}
                              >
                                Extend goal
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )
                }
                return (
                  <div className="goalsMetricRows">
                    {crossPlatformMetrics.length > 0 && (
                      <div className="goalsMetricSubSection goalsMetricSubSection--crossPlatform">
                        <div className="goalsMetricSubSectionLabel">Cross-platform goals</div>
                        <div className="goalsMetricSubSectionRows">
                          {crossPlatformMetrics.map(renderMetricCard)}
                        </div>
                      </div>
                    )}
                    {regularMetrics.length > 0 && (
                      <div className="goalsMetricSubSection">
                        <div className="goalsMetricSubSectionRows">
                          {regularMetrics.map(renderMetricCard)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          )
        })}
      </div>
    </>
  )
}
